/**
 * Types for the Refunds API module.
 *
 * A refund gives money back to the renter of a reservation, whatever way it
 * went back (back to the card through Stripe, Zelle, cash, check, ...). One
 * refund can take money from several payments of the reservation's rental
 * order: the refund holds the reason and who recorded it, and each payment
 * row holds the money, the method, the day it went back and, for a row going
 * back to the card, Stripe's state. Mirrors the JSON of
 * `GET/POST /v1/rentals/:rental_id/refunds`.
 */

import { PermissionAction } from "../permissions/types";

/** Why the money went back. A closed list; `other` needs a `reason_text`. */
export const REFUND_REASON_TYPES = [
  "cancellation",
  "ended_early",
  "vehicle_issue",
  "pricing_error",
  "duplicate",
  "goodwill",
  "requested_by_customer",
  "other",
] as const;

export type RefundReasonType = (typeof REFUND_REASON_TYPES)[number];

/**
 * How staff can say the money went back outside FleetSync. `stripe` is not
 * among them: it is not recorded, it is sent to Stripe (FLEE-285), and only
 * for a card payment. `other` needs a free-text `method_label`.
 */
export const REFUND_RECORDED_METHODS = [
  "zelle",
  "cash",
  "check",
  "bank_transfer",
  "card_terminal",
  "other",
] as const;

export type RefundRecordedMethod = (typeof REFUND_RECORDED_METHODS)[number];

/** Every method a stored refund row can carry, Stripe included. */
export type RefundMethod = RefundRecordedMethod | "stripe";

/**
 * Where a row going back to the card stands in Stripe (FLEE-285), as Stripe
 * names it. `failed` and `canceled` rows gave nothing back: they are shown
 * but never counted as refunded.
 */
export const REFUND_STRIPE_STATUSES = [
  "pending",
  "requires_action",
  "succeeded",
  "failed",
  "canceled",
] as const;

export type RefundStripeStatus = (typeof REFUND_STRIPE_STATUSES)[number];

/**
 * The server issues `rentals:record_refund` as its own permission, keyed by
 * the path below with the `modify` action. Like the external payment
 * permissions it is a sibling of `/reservations`, so check it with
 * `strictMatch`: without it, `canPerform` falls back to the `/reservations`
 * permission and every user who can modify reservations would pass. Fleet
 * owners always pass.
 */
export const RECORD_REFUND_PERMISSION = {
  entity: "/reservations/record-refund",
  action: PermissionAction.MODIFY,
} as const;

/**
 * The money one payment gives back outside FleetSync. `amount` is a decimal
 * string with at most two decimals. `method_label` is required for `other`.
 * `refunded_on` is `YYYY-MM-DD`; the server defaults it to today in the
 * fleet's zone.
 */
export interface RecordRecordedRefundPaymentParams {
  /** The payment's public id. */
  payment_id: string;
  amount: string;
  method: RefundRecordedMethod;
  method_label: string | null;
  reference: string | null;
  refunded_on?: string;
}

/**
 * The money one card payment gives back through Stripe (FLEE-285): only a
 * card payment can. It carries no label, reference or date: the server
 * refuses them, and dates the row today in the fleet's zone.
 */
export interface RecordStripeRefundPaymentParams {
  /** The payment's public id. */
  payment_id: string;
  amount: string;
  method: "stripe";
  method_label?: never;
  reference?: never;
  refunded_on?: never;
}

/** The money one payment gives back. */
export type RecordRefundPaymentParams =
  | RecordRecordedRefundPaymentParams
  | RecordStripeRefundPaymentParams;

/**
 * What a refund by line items gives back (FLEE-436): nights by date
 * (`YYYY-MM-DD`) and add-ons, fees and charges by the `key` of
 * `GET .../refunds/refundable`. Only the set of nights reaches the server;
 * how staff picked them is a screen choice.
 */
export interface RefundLinesParams {
  nights: string[];
  items: string[];
}

/**
 * Request body (flat JSON). The payment rows must add up to `amount`, and a
 * payment never gives back more than what is left on it. `reason_text` is
 * required when `reason_type` is `other`, up to 1000 characters.
 *
 * With `lines`, the server prices every line itself (tax per line) and
 * `amount` must equal that total. Without them it is a refund by amount.
 */
export interface RecordRefundParams {
  amount: string;
  reason_type: RefundReasonType;
  reason_text: string | null;
  payments: RecordRefundPaymentParams[];
  lines?: RefundLinesParams | null;
  /**
   * One key per refund the client submits, the same on a retry of it
   * (FLEE-481): the server returns the refund it already recorded with the
   * key instead of a second one. Up to 255 characters.
   */
  idempotency_key?: string;
}

/**
 * Who or what created the refund: staff (`fleet_user`), or Stripe for a
 * refund made in the Stripe dashboard (`stripe`, with no id and no name).
 */
export interface RefundCreatedBy {
  type: "fleet_user" | "stripe" | "rvshare" | "system" | (string & {});
  id: string | null;
  name: string | null;
}

/** The money that came back from one payment. */
export interface RefundPayment {
  id: string;
  /** The payment's public id. */
  payment_id: string;
  amount: string;
  currency: string;
  method: RefundMethod;
  method_label: string | null;
  reference: string | null;
  /**
   * `YYYY-MM-DD`, the day the money went back; for a row going back to the
   * card, the day it was sent to Stripe (or Stripe made it).
   */
  refunded_on: string;
  /**
   * Stripe's state for a row going back to the card (`method` `stripe`);
   * null for every other method. A server from before FLEE-285 leaves it out.
   */
  stripe_status?: RefundStripeStatus | null;
  /** Stripe's refund id (`re_...`) once Stripe has the refund. */
  stripe_refund_id?: string | null;
  /**
   * Why Stripe could not send it back: Stripe's reason or error code, or
   * FleetSync's own `not_sent` / `no_merchant_account` for a row it could not
   * send to Stripe (FLEE-496).
   */
  failure_reason?: string | null;
  /**
   * How FleetSync's sends of a `stripe` row went (FLEE-496); null for every
   * other method. A server from before FLEE-496 leaves it out.
   */
  send_tries?: RefundSendTries | null;
  /**
   * Whether staff can retry the row now: FleetSync gave up sending it, the
   * fleet's Stripe account is connected, and its money was not given back
   * another way since (FLEE-496).
   */
  retryable?: boolean;
}

/**
 * What came of FleetSync's newest failed try: `refused` (Stripe answered and
 * made nothing), `no_answer` (Stripe may have made it) or `not_reached` (the
 * request never left FleetSync).
 */
export interface RefundSendError {
  outcome: "refused" | "no_answer" | "not_reached" | (string & {});
  /** Stripe's code or type, or FleetSync's own (`no_merchant_account`). */
  code: string | null;
  message: string | null;
  /** ISO 8601. */
  at: string;
}

/** Every try FleetSync made to send a row back to the card (FLEE-496). */
export interface RefundSendTries {
  /** Tries to reach Stripe; reads of Stripe's list do not count. */
  count: number;
  last_try_at: string | null;
  /** When FleetSync tries again on its own; null when it will not. */
  next_try_at: string | null;
  last_error: RefundSendError | null;
  retried_at: string | null;
  retried_by: { id: string; name: string } | null;
}

/** `POST .../refund-payments/:id/retry`: the refund with the row's new state. */
export interface RetryRefundPaymentResponse {
  data: Refund;
}

/** The kind of a refunded line. */
export type RefundLineType = "night" | "addon" | "fee" | "custom";

/**
 * One thing a refund gave back, as the server copied it when the refund was
 * recorded: a night (by `night_date`) or an add-on, fee or charge (by `key`).
 * Amounts are decimal strings; `tax_amount` is the tax refunded with it.
 */
export interface RefundLine {
  id: string;
  line_type: RefundLineType;
  /** `night:2026-09-06`, `addon:<id>`, `fee:<...>`, `custom:<...>`. */
  key: string;
  /** "Night of Sep 6, 2026", "Generator", "Delivery fee". */
  description: string;
  /** `YYYY-MM-DD` for a night, null otherwise. */
  night_date: string | null;
  quantity: number;
  amount: string;
  taxable: boolean;
  tax_amount: string;
}

export interface Refund {
  id: string;
  /** The rental's public id. */
  rental_id: string;
  /** The rental order's public id. */
  order_id: string;
  amount: string;
  currency: string;
  reason_type: RefundReasonType;
  reason_text: string | null;
  created_by: RefundCreatedBy;
  /** ISO 8601. */
  inserted_at: string;
  payments: RefundPayment[];
  /**
   * What was refunded (FLEE-436), nights by date then items by description.
   * Empty for a refund by amount.
   */
  lines: RefundLine[];
}

export interface RecordRefundResponse {
  data: Refund;
}

/** The rental's refunds, newest first. */
export interface RefundListResponse {
  data: Refund[];
}

/**
 * A night of the stay a refund by line items can give back: its price as
 * charged (`fleet_rental_daily_rates`), whether it is taxed, and whether an
 * earlier refund already gave it back.
 */
export interface RefundableNight {
  /** `YYYY-MM-DD`. */
  date: string;
  amount: string;
  taxable: boolean;
  refunded: boolean;
}

/** An add-on, fee or charge a refund by line items can give back whole. */
export interface RefundableItem {
  /**
   * Opaque and stable: the same after the order's line items are rebuilt.
   * Sent back in `lines.items`.
   */
  key: string;
  line_type: Exclude<RefundLineType, "night">;
  description: string;
  amount: string;
  taxable: boolean;
  refunded: boolean;
  /**
   * The public id of the order line it stands for now, for display only
   * (never sent back): it changes when the order's line items are rebuilt.
   */
  line_item_id: string | null;
}

/**
 * What can be refunded by line on a rental (`GET .../refunds/refundable`).
 * Insurance, commission, deposit, tax and discount lines are not refundable
 * by line.
 */
export interface RentalRefundable {
  currency: string;
  /**
   * The order's sales tax rate as a decimal string ("0.086"), "0" without
   * tax. Tax per line: half up to the cent of amount × rate when taxable.
   */
  tax_rate: string;
  /** The public id of the order's current rental line ("Vehicle Rental"). */
  rental_line_item_id: string | null;
  /** One per night of the stay, from pick-up to the night before return. */
  nights: RefundableNight[];
  items: RefundableItem[];
}

export interface RentalRefundableResponse {
  data: RentalRefundable;
}

/** Codes the record endpoint answers with. */
export type RefundErrorCode =
  | "rental_not_found"
  | "payment_not_found"
  | "payments_total_mismatch"
  | "amount_exceeds_refundable"
  | "payment_not_refundable"
  | "lines_total_mismatch"
  | "line_not_refundable"
  | "line_already_refunded"
  | "managed_externally"
  /** A row goes back to the card and the fleet has no Stripe account. */
  | "no_merchant_account"
  /**
   * 409: the `idempotency_key` was already used on this rental for a refund
   * of other money (FLEE-493).
   */
  | "idempotency_key_mismatch";

export interface RefundErrorInfo {
  /** The server `error.code`, when the failure carries one. */
  code: RefundErrorCode | (string & {}) | null;
  /** The server's own message for a code error, or a 403/Error message. */
  message: string | null;
  /** Extra data of a code error, e.g. `{ payment_id, refundable }`. */
  payload: Record<string, unknown> | null;
  /** First message per request field of a 422 `errors` map. */
  fieldErrors: Partial<
    Record<keyof RecordRefundParams | (string & {}), string>
  >;
  /**
   * First message per field of each payment row, at the row's position in the
   * request. Empty when the server sent no per-row errors.
   */
  paymentErrors: Partial<
    Record<keyof RecordRefundPaymentParams | (string & {}), string>
  >[];
}
